import React, { useState, useEffect } from 'react';
import {
  Dialog, DialogTitle, DialogContent, DialogActions, Divider,
  Button, Box, Typography, Checkbox, FormGroup, FormControlLabel, Grid, Alert
} from '@mui/material';
import api from '../../config/api';
import { MODULE_GROUPS, VISIBLE_MODULE_KEYS } from '../../config/modules';
import { pagesForModule } from '../../config/navigation';

const CompanyModulesDialog = ({ open, onClose, company, onSaved }) => {
  const [selected, setSelected] = useState([]);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    if (open && company) {
      setError('');
      setSelected(Array.isArray(company.enabled_modules) ? [...company.enabled_modules] : []);
    }
  }, [open, company]);

  if (!company) return null;

  const handleToggle = (key) => {
    setSelected(prev => prev.includes(key)
      ? prev.filter(k => k !== key)
      : [...prev, key]);
  };

  const handleSelectAll = () => setSelected([...VISIBLE_MODULE_KEYS]);
  const handleClear = () => setSelected([]);

  const handleSave = async () => {
    setSaving(true);
    setError('');
    try {
      await api.put(`/api/users/admin/companies/${company.id}/modules`, {
        enabled_modules: selected
      });
      if (onSaved) onSaved({ ...company, enabled_modules: selected });
      onClose();
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to save');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth PaperProps={{ sx: { borderRadius: 3 } }}>
      <DialogTitle>
        <Typography variant="h6" sx={{ fontWeight: 600 }}>
          Edit Modules — {company.name}
        </Typography>
        <Typography variant="body2" color="text.secondary">
          Empty selection = no restriction (company sees all visible modules).
          Non-empty = whitelist; users in this company can only ever access modules on this list,
          intersected with their per-user permissions.
        </Typography>
      </DialogTitle>
      <Divider />
      <DialogContent>
        {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
        <Box sx={{ mb: 2, display: 'flex', gap: 1 }}>
          <Button size="small" variant="outlined" onClick={handleSelectAll} disabled={saving}>
            Select all visible
          </Button>
          <Button size="small" variant="outlined" onClick={handleClear} disabled={saving}>
            Clear (no restriction)
          </Button>
        </Box>

        {MODULE_GROUPS.map(group => (
          <React.Fragment key={group.label}>
            <Typography
              variant="caption"
              sx={{
                fontWeight: 600, color: 'text.secondary',
                textTransform: 'uppercase', letterSpacing: 0.5,
                mt: 1, display: 'block'
              }}
            >
              {group.label}
            </Typography>
            <FormGroup>
              <Grid container>
                {group.modules.map(({ key, label }) => {
                  const pages = pagesForModule(key);
                  return (
                    <Grid item xs={4} key={key}>
                      <FormControlLabel
                        control={
                          <Checkbox
                            checked={selected.includes(key)}
                            onChange={() => handleToggle(key)}
                            disabled={saving}
                            size="small"
                          />
                        }
                        label={
                          <Box>
                            <Typography variant="body2">{label}</Typography>
                            {pages.length > 1 && (
                              <Typography variant="caption" color="text.secondary">
                                {pages.join(', ')}
                              </Typography>
                            )}
                          </Box>
                        }
                      />
                    </Grid>
                  );
                })}
              </Grid>
            </FormGroup>
          </React.Fragment>
        ))}
      </DialogContent>
      <Divider />
      <DialogActions sx={{ p: 2 }}>
        <Button onClick={onClose} variant="outlined" disabled={saving}>Cancel</Button>
        <Button onClick={handleSave} variant="contained" disabled={saving}>
          {saving ? 'Saving…' : 'Save'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default CompanyModulesDialog;
